import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Alert,
  Snackbar,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Wc,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNav from '../components/BottomNavigation';
import EnhancedLocationDialog from '../components/EnhancedLocationDialog';
import useResponsive from '../hooks/useResponsive';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form states
  const [profilePhoto, setProfilePhoto] = useState(user?.profilePhotoUrl || user?.avatar || '');
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    location: ''
  });

  // Store location coordinates separately
  const [locationCoordinates, setLocationCoordinates] = useState(null);

  // Update form data when user changes
  useEffect(() => {
    if (!user) return;

    const locationAddress = user?.location?.address || (typeof user?.location === 'string' ? user.location : '') || '';
    
    setFormData(prev => ({
      ...prev,
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
      gender: user?.gender || '',
      location: locationAddress
    }));
    
    // Check if we have a base64 profile picture in localStorage (from recent upload)
    const localProfileImage = localStorage.getItem('customerProfileImage');
    
    // Use local base64 image if available, otherwise use server image
    const profilePictureUrl = localProfileImage || user?.profilePhotoUrl || user?.avatar || '';
    setProfilePhoto(profilePictureUrl);
    
    setLocationCoordinates(user?.location?.coordinates?.coordinates || null);
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('📸 Profile picture upload started:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    try {
      setLoading(true);
      
      // First, convert the file to base64 for immediate display
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result;
        
        // Update local state immediately with base64 data
        setProfilePhoto(base64Data);
        
        // Store base64 data in localStorage for other components
        localStorage.setItem('customerProfileImage', base64Data);
        
        // Trigger event for other components
        const profileUpdateEvent = new CustomEvent('profilePictureUpdated', {
          detail: { profilePicture: base64Data }
        });
        window.dispatchEvent(profileUpdateEvent);
      };
      
      reader.readAsDataURL(file);
      
      // Then upload to server
      const formDataPhoto = new FormData();
      formDataPhoto.append('profilePhoto', file);
      
      const response = await authAPI.updateProfilePhoto(formDataPhoto);
      
      // Update profile data with server response if it contains a profile picture URL
      if (response.success && response.data?.profilePhotoUrl) {
        const serverUrl = response.data.profilePhotoUrl;
        
        // Ensure the URL is complete (add base URL if it's a relative path)
        let finalUrl = serverUrl;
        if (serverUrl && !serverUrl.startsWith('http')) {
          const baseURL = process.env.REACT_APP_API_URL || 'https://api.nistylez.com';
          finalUrl = serverUrl.startsWith('/') ? `${baseURL}${serverUrl}` : `${baseURL}/${serverUrl}`;
        }
        
        setProfilePhoto(finalUrl);
        
        // Also update localStorage with the server URL for consistency
        localStorage.setItem('customerProfileImage', finalUrl);
        
        // Update user context with new photo
        if (updateUser) {
          const updatedUser = { ...user, profilePhotoUrl: finalUrl };
          updateUser(updatedUser);
        }
        
        // Trigger event for other components with server URL
        const profileUpdateEvent = new CustomEvent('profilePictureUpdated', {
          detail: { profilePicture: finalUrl }
        });
        window.dispatchEvent(profileUpdateEvent);
      }
      
      setSnackbar({
        open: true,
        message: 'Profile picture updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating profile picture:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update profile picture',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };


  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const profileData = {
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        location: {
          address: formData.location,
          ...(locationCoordinates && { coordinates: locationCoordinates })
        }
      };

      const response = await authAPI.updateProfile(profileData);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Profile updated successfully!',
          severity: 'success'
        });
        
        // Update user context with new data
        if (updateUser) {
          const updatedUserData = { ...user, ...response.data };
          updateUser(updatedUserData);
        }
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to update profile.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to update profile. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    // Navigate back to profile page
    navigate('/profile');
  };

  const handleLocationSelect = (locationData) => {
    setFormData(prev => ({
      ...prev,
      location: locationData.address
    }));
    
    // Extract coordinates from locationData (it has lat/lng properties)
    if (locationData.lat && locationData.lng) {
      const coordinates = [locationData.lng, locationData.lat]; // [longitude, latitude] format for GeoJSON
      setLocationCoordinates(coordinates);
    }
    
    setShowLocationDialog(false);
  };


  const renderEditableField = (label, field, value, icon, type = 'text', options = null, readOnly = false) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
        {icon && <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        {label}
      </Typography>
      {type === 'select' ? (
        <FormControl fullWidth>
          <Select
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            disabled={readOnly}
            sx={{
              backgroundColor: readOnly ? '#f5f5f5' : 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: readOnly ? '#e0e0e0' : '#006AF9'
              }
            }}
          >
            {options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          fullWidth
          type={type}
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          variant="outlined"
          disabled={readOnly}
          InputProps={{
            sx: {
              backgroundColor: readOnly ? '#f5f5f5' : 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: readOnly ? '#e0e0e0' : '#006AF9'
              }
            }
          }}
          sx={{
            '& .MuiInputBase-input': {
              color: readOnly ? '#666' : '#333'
            }
          }}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ pb: 8 }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: '#0A2A66', 
        color: 'white', 
        p: { xs: 2, md: 3 },
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            color="inherit" 
            onClick={() => navigate('/profile')}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
              }}
          >
            <ArrowBack />
          </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Edit Profile
          </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
          Update your profile information and photo
        </Typography>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            {/* Profile Photo Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Profile Photo
              </Typography>
              
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                <Avatar
                  src={profilePhoto}
                  sx={{
                    width: 120,
                    height: 120,
                    border: '3px solid #006AF9',
                    fontSize: '3rem'
                  }}
                >
                  {user?.firstName?.charAt(0) || 'U'}
                </Avatar>
                
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#006AF9',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0056CC' }
                  }}
                >
                  <PhotoCamera />
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </IconButton>
              </Box>

              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                component="label"
                disabled={loading}
                sx={{
                  borderColor: '#006AF9',
                  color: '#006AF9',
                  '&:hover': {
                    borderColor: '#0056CC',
                    backgroundColor: 'rgba(0, 106, 249, 0.04)'
                  }
                }}
              >
                {loading ? 'Uploading...' : 'Change Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Profile Information Section */}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Profile Information
              </Typography>
              
                <Grid container spacing={2}>
                <Grid item xs={12}>
                  {renderEditableField('Full Name', 'name', `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Not provided', <Person sx={{ color: '#006AF9' }} />, 'text', null, true)}
                  </Grid>
                  <Grid item xs={12}>
                  {renderEditableField('Email Address', 'email', user?.email || 'Not provided', <Email sx={{ color: '#006AF9' }} />, 'text', null, true)}
                  </Grid>
                  <Grid item xs={12}>
                  {renderEditableField('Phone Number', 'phone', user?.phone || 'Not provided', <Phone sx={{ color: '#006AF9' }} />, 'text', null, true)}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderEditableField('Date of Birth', 'dateOfBirth', formData.dateOfBirth, <CalendarToday sx={{ color: '#006AF9' }} />, 'date')}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderEditableField('Gender', 'gender', formData.gender, <Wc sx={{ color: '#006AF9' }} />, 'select', [
                      { value: '', label: 'Select Gender' },
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
                    ])}
                  </Grid>
                  <Grid item xs={12}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <LocationOn sx={{ color: '#006AF9', mr: 1 }} />
                      Location
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        key={`location-${formData.location || 'empty'}`}
                        fullWidth
                        value={formData.location || ''}
                        variant="outlined"
                        placeholder="Select your location"
                        InputProps={{
                          readOnly: true,
                          sx: {
                            backgroundColor: '#f5f5f5',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e0e0e0'
                            }
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            color: formData.location ? '#000' : '#666'
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => setShowLocationDialog(true)}
                        sx={{
                          borderColor: '#006AF9',
                          color: '#006AF9',
                          minWidth: 'auto',
                          px: 2,
                          '&:hover': {
                            borderColor: '#0056CC',
                            backgroundColor: 'rgba(0, 106, 249, 0.04)'
                          }
                        }}
                      >
                        <LocationOn />
                      </Button>
                    </Box>
                  </Box>
                </Grid>
                  </Grid>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSaveProfile}
                        disabled={loading}
                        sx={{
                          backgroundColor: '#006AF9',
                  borderRadius: '12px',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { 
                    backgroundColor: '#0056CC',
                    boxShadow: '0 4px 12px rgba(0, 106, 249, 0.3)'
                  }
                        }}
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Profile'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancelEdit}
                        disabled={loading}
                        sx={{
                          borderColor: '#666',
                          color: '#666',
                  borderRadius: '12px',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                          '&:hover': {
                            borderColor: '#333',
                            backgroundColor: 'rgba(102, 102, 102, 0.04)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Location Dialog */}
      <EnhancedLocationDialog
        open={showLocationDialog}
        onClose={() => setShowLocationDialog(false)}
        onLocationSelect={handleLocationSelect}
        currentAddress={formData.location}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditProfile;
